import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import api from '../utils/api';

export default function UnifiedLogin() {
    const navigate = useNavigate();
    const { setAuth } = useAuthStore();
    const [loginType, setLoginType] = useState('whmcs'); // 'whmcs' ou 'internal'
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            let response;
            
            if (loginType === 'whmcs') {
                // Connexion WHMCS (contributeurs)
                console.log('🔐 Connexion WHMCS...');
                response = await api.post('/whmcs/auth', {
                    email: formData.email,
                    password: formData.password
                });
            } else {
                // Connexion interne (admin/rédacteurs)
                console.log('🔐 Connexion interne...');
                response = await api.post('/auth/login', {
                    email: formData.email,
                    password: formData.password
                });
            }

            if (response.data.success) {
                setAuth(response.data.user, response.data.token);
                console.log('✅ Connexion réussie!', response.data.user);
                navigate('/admin');
            }
        } catch (err) {
            console.error('❌ Erreur connexion:', err);
            setError(err.response?.data?.error || 'Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-4">
                        <img src="https://sunserv.fr/assets/icon.png" alt="SunServ Logo" className="w-12 h-12" />
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-2">
                        Sun<span className="text-yellow-400">Serv</span>
                    </h1>
                    <p className="text-gray-400">Connexion au blog</p>
                </div>

                {/* Card de connexion */}
                <div className="bg-surface rounded-2xl shadow-2xl border border-gray-700/50 p-8">
                    {/* Sélecteur de type de connexion */}
                    <div className="flex gap-2 mb-6 p-1 bg-background rounded-lg">
                        <button
                            type="button"
                            onClick={() => setLoginType('whmcs')}
                            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                                loginType === 'whmcs'
                                    ? 'bg-yellow-400 text-gray-900'
                                    : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            <i className="fas fa-users mr-2"></i>
                            Client SunServ
                        </button>
                        <button
                            type="button"
                            onClick={() => setLoginType('internal')}
                            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                                loginType === 'internal'
                                    ? 'bg-yellow-400 text-gray-900'
                                    : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            <i className="fas fa-user-shield mr-2"></i>
                            Équipe
                        </button>
                    </div>

                    {/* Info bannière */}
                    {loginType === 'whmcs' && (
                        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                            <p className="text-yellow-400 text-sm flex items-start gap-2">
                                <i className="fas fa-info-circle mt-0.5"></i>
                                <span>
                                    <strong>Clients SunServ :</strong> Utilisez vos identifiants de l'espace client.
                                    Gagnez <strong>1€</strong> de crédit par article publié !
                                </span>
                            </p>
                        </div>
                    )}

                    {loginType === 'internal' && (
                        <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                            <p className="text-blue-400 text-sm flex items-start gap-2">
                                <i className="fas fa-shield-alt mt-0.5"></i>
                                <span>
                                    <strong>Espace réservé</strong> à l'équipe SunServ (administrateurs et rédacteurs).
                                </span>
                            </p>
                        </div>
                    )}

                    {/* Formulaire */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {error && (
                            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <p className="text-red-400 text-sm flex items-center gap-2">
                                    <i className="fas fa-exclamation-circle"></i>
                                    {error}
                                </p>
                            </div>
                        )}

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-background border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                                placeholder={loginType === 'whmcs' ? 'votre@email.com' : 'admin@sunserv.xyz'}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                                Mot de passe
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-background border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                                placeholder="••••••••"
                                required
                                disabled={loading}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-gray-900 font-bold py-3 px-4 rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                    Connexion...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-sign-in-alt mr-2"></i>
                                    Se connecter
                                </>
                            )}
                        </button>
                    </form>

                    {/* Footer */}
                    <div className="mt-6 pt-6 border-t border-gray-700">
                        <p className="text-center text-gray-400 text-sm">
                            {loginType === 'whmcs' ? (
                                <>
                                    Pas encore de compte ?{' '}
                                    <Link to="/register" className="text-yellow-400 hover:text-yellow-300">
                                        S'inscrire gratuitement
                                    </Link>
                                </>
                            ) : (
                                <>
                                    Besoin d'aide ?{' '}
                                    <a href="mailto:support@sunserv.fr" className="text-yellow-400 hover:text-yellow-300">
                                        Contacter le support
                                    </a>
                                </>
                            )}
                        </p>
                        <p className="text-center text-gray-500 text-xs mt-3">
                            <Link to="/" className="hover:text-gray-300 transition-colors">
                                <i className="fas fa-arrow-left mr-1"></i>
                                Retour au blog
                            </Link>
                        </p>
                    </div>
                </div>

                {/* Bénéfices contributeur */}
                {loginType === 'whmcs' && (
                    <div className="mt-6 p-4 bg-surface/50 rounded-lg border border-gray-700/30">
                        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                            <i className="fas fa-gift text-yellow-400"></i>
                            Avantages contributeur
                        </h3>
                        <ul className="space-y-2 text-sm text-gray-300">
                            <li className="flex items-center gap-2">
                                <i className="fas fa-check text-green-400"></i>
                                <span>Récompense automatique de 1€ par article publié</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <i className="fas fa-check text-green-400"></i>
                                <span>Crédit utilisable sur tous vos services SunServ</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <i className="fas fa-check text-green-400"></i>
                                <span>Validation rapide par notre équipe</span>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
